import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useFetch } from 'use-http'

import { Box, Card, Heading, Stack, Text, TextField } from '@myxplor/stardust'

import sortObjectsBy from '~/src/utils/sortObjectsBy'

import LoadingLoop from '~/src/components/LoadingLoop'

const LINK_STYLE = {
  color: 'inherit',
  textDecoration: 'none',
  width: '100%',
}

const Services = () => {
  const [searchText, setSearchText] = useState('')

  const accountRequest = useFetch('/api/v1/accounts', {}, [])
  const serviceRequest = useFetch('/api/v1/services', {}, [])
  const vendorRequest = useFetch('/api/v1/vendors', {}, [])

  const accountLookup = useMemo(() => {
    const accounts = (accountRequest.data?.data || []) as Papi.Account[]
    return accounts.reduce((acc, account) => {
      return { ...acc, [account.id]: account }
    }, {}) as Record<number, Papi.Account>
  }, [accountRequest.data])

  const vendorLookup = useMemo(() => {
    const vendors = (vendorRequest.data?.data || []) as Papi.Vendor[]
    return vendors.reduce((acc, vendor) => {
      return { ...acc, [vendor.id]: vendor }
    }, {}) as Record<number, Papi.Vendor>
  }, [vendorRequest.data])

  const services = useMemo(() => {
    const services = (serviceRequest.data?.data || []) as Papi.Service[]
    const lowerSearchText = searchText.toLowerCase()
    const filteredServices = services.filter((service) => {
      const account = accountLookup[service.account_id]
      const vendor = vendorLookup[account?.vendor_id]
      return (
        service.name.toLowerCase().includes(lowerSearchText) ||
        account?.name.toLowerCase().includes(lowerSearchText) ||
        vendor?.name.toLowerCase().includes(lowerSearchText)
      )
    })
    return sortObjectsBy(filteredServices, 'name')
  }, [accountLookup, searchText, serviceRequest.data, vendorLookup])

  if (accountRequest.loading || serviceRequest.loading || vendorRequest.loading) {
    return <LoadingLoop />
  }

  if (accountRequest.error || serviceRequest.error || vendorRequest.error) {
    return <div>Error...</div>
  }

  return (
    <Stack space="small">
      <Box alignItems="center" display="flex" justifyContent="space-between" width={1}>
        <Heading level={4}>Services</Heading>
      </Box>
      <TextField
        label="Search"
        name="search"
        value={searchText}
        onChange={(value: string) => setSearchText(value)}
      />
      <Card>
        <Stack space="none">
          {services.map((service, index) => {
            const account = accountLookup[service.account_id]
            const vendor = vendorLookup[account.vendor_id]

            return (
              <Link key={service.id} style={LINK_STYLE} to={`/services/${service.id}`}>
                <Box
                  alignItems="center"
                  borderTop={index === 0 ? undefined : '1px solid'}
                  borderColor={index === 0 ? undefined : 'lowerEmphasis'}
                  cursor="pointer"
                  display="flex"
                  hoverStyle={({ theme }) => ({ backgroundColor: theme.colors.lowestEmphasis })}
                  justifyContent="space-between"
                  padding="small"
                  transition="background-color .2s"
                  width={1}>
                  <Box flexGrow={1}>
                    <Text>{service.name}</Text>
                  </Box>
                  <Box display="flex" flexDirection="column" paddingRight="medium">
                    <Text align="right" prefab="overline">
                      Native ID
                    </Text>
                    <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                      {service.origin_id}
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="column" paddingRight="medium">
                    <Text align="right" prefab="overline">
                      Account
                    </Text>
                    <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                      {account.name}
                    </Text>
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <Text align="right" prefab="overline">
                      BMS
                    </Text>
                    <Text align="right" lineHeight={1.4} prefab="bodyAlt">
                      {vendor.name}
                    </Text>
                  </Box>
                </Box>
              </Link>
            )
          })}
        </Stack>
      </Card>
    </Stack>
  )
}

export default React.memo(Services)
